import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { FeedItem, Me } from "@scrile/contracts";
import { useApi } from "../api/context";
import { Avatar } from "../components/Avatar";
import { Icon } from "../components/Icon";
import { Link } from "../components/Link";
import { Spinner } from "../components/Spinner";
import { t, tn } from "../i18n";
import { trackOnce } from "../lib/analytics";
import { hasAccess, isSubscribed } from "../lib/episodes";
import { initial } from "../lib/format";
import { trialDaysFor } from "../lib/money";
import { href, navigate, type Route } from "../lib/router";
import { Composer, type ComposerHandle } from "../messenger/Composer";
import { DayRow, EpisodeRow, MessageRow, NoticeRow, PaywallRow, TypingRow, type BubbleHandlers } from "../messenger/Bubbles";
import { messageMaxLength } from "../messenger/feedItems";
import { Lightbox } from "../messenger/Lightbox";
import { PaywallDock } from "../messenger/PaywallDock";
import { activePaywall, buildTimeline } from "../messenger/timeline";
import type { TypingKind } from "../messenger/typing";
import { useChatViewport } from "../messenger/useChatViewport";
import { useMessenger } from "../messenger/useMessenger";
import { usePlayerActions } from "../player/PlayerContext";
import { useHeroData } from "../state/heroData";
import { useToast } from "../state/toast";

type ChatRoute = Extract<Route, { name: "chat" }>;

/** Ближе к низу, чем на столько, — «внизу»: новое сообщение прокручивает ленту само. */
const BOTTOM_SLACK_PX = 72;
const CONFIRM_ATTEMPTS = 8;
const CONFIRM_DELAY_MS = 1_500;
const MAX_LENGTH = messageMaxLength();

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Что сказать экранному диктору о новом элементе: коротко, без повторения всей ленты. */
function announcementFor(item: FeedItem, heroName: string): string | null {
  if (item.from === "user") return null;
  switch (item.kind) {
    case "text": {
      const text = item.body.text.length > 160 ? `${item.body.text.slice(0, 157)}…` : item.body.text;
      return t("messenger.announce.text", { hero: heroName, text });
    }
    case "voice":
      return t("messenger.announce.voice", { hero: heroName });
    case "photo":
      return t("messenger.announce.photo", { hero: heroName });
    case "video":
      return t("messenger.announce.video", { hero: heroName });
    case "hero_card":
      return t("messenger.announce.card", { hero: heroName, name: item.body.name });
    case "episode_ready":
      return t("messenger.episodeStarted", { n: item.body.episode });
    case "system":
      if (item.body.type === "reminder" && item.body.text) return t("messenger.announce.text", { hero: heroName, text: item.body.text });
      if (item.body.type === "busy") return item.body.text?.trim() || t("messenger.busy", { hero: heroName });
      if (item.body.type === "paywall") return t("messenger.paywall.line");
      return item.body.text ?? null;
    default:
      return null;
  }
}

function typingText(kind: TypingKind): string {
  switch (kind) {
    case "recording":
      return t("messenger.recording");
    case "sending_photo":
      return t("messenger.sendingPhoto");
    case "sending_video":
      return t("messenger.sendingVideo");
    default:
      return t("messenger.typing");
  }
}

/** Последняя начавшаяся серия — подпись под именем героя в шапке. */
function currentEpisode(items: readonly FeedItem[]): number | null {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const item = items[i]!;
    if (item.kind === "episode_ready") return item.body.episode;
    if (item.from === "hero" && (item.kind === "text" || item.kind === "voice" || item.kind === "photo" || item.kind === "video" || item.kind === "hero_card")) {
      if (item.body.episode) return item.body.episode;
    }
  }
  return null;
}

/**
 * Шаблон «Мессенджер»: переписка с героем. Лента outbox (long-poll), пузыри с паузами и
 * «печатает…», ввод текста и голосового, фото на весь экран, карточки героев, пейвол
 * после бесплатных серий на месте поля ввода.
 *
 * Экран — на всю видимую высоту (клавиатура iPhone его не перекрывает, см.
 * useChatViewport); прокручивается только лента. Новое сообщение прокручивает её, только
 * если человек внизу; иначе — кнопка «N new messages».
 */
export function Messenger({ route, me }: { route: ChatRoute; me: Me }) {
  const api = useApi();
  const { hero, episodes, reloadMe, setMe } = useHeroData();
  const toast = useToast();
  const player = usePlayerActions();

  const root = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const log = useRef<HTMLOListElement>(null);
  const composer = useRef<ComposerHandle>(null);
  const atBottom = useRef(true);
  const [pinnedToBottom, setPinnedToBottom] = useState(true);
  const [unread, setUnread] = useState(0);
  const [announcement, setAnnouncement] = useState("");
  const [photo, setPhoto] = useState<{ url: string; trigger: HTMLElement } | null>(null);
  const forceScroll = useRef(false);

  const access = hasAccess(me);
  const subscribed = isSubscribed(me);

  const messenger = useMessenger({
    api,
    userId: me.id,
    heroSlug: hero.slug,
    onPaymentRequired: () => { void reloadMe().catch(() => null); },
    onArrived: (items, meta) => {
      if (meta.history) return;
      const fromHero = items.filter((item) => item.from === "hero");
      if (fromHero.length === 0) return;
      const last = fromHero.map((item) => announcementFor(item, hero.name)).filter(Boolean).at(-1);
      if (last) setAnnouncement(last);
      if (!atBottom.current) setUnread((count) => count + fromHero.filter((item) => item.kind !== "episode_ready").length);
    },
  });

  const episodeTitles = useMemo(() => new Map(episodes.map((episode) => [episode.ordinal, episode.title])), [episodes]);
  const entries = useMemo(
    () =>
      buildTimeline({
        items: messenger.items,
        outgoing: messenger.outgoing,
        timeZone: me.timezone,
        now: Date.now(),
        heroName: hero.name,
        episodeTitles,
      }),
    [messenger.items, messenger.outgoing, me.timezone, hero.name, episodeTitles],
  );

  // Пейвол: сюжет дошёл до платной серии (элемент ленты) или отправка получила 402
  const feedPaywall = activePaywall(entries);
  const paywall = messenger.paymentRequired || (!access && feedPaywall !== null) ? { episode: feedPaywall?.episode ?? null } : null;
  const { clearPaymentRequired } = messenger;
  useEffect(() => {
    if (access) clearPaymentRequired();
  }, [access, clearPaymentRequired]);

  // Пилот переписки — один раз за заход; звук сайта (трейлер) — стоп: у переписки свой
  useEffect(() => {
    if (!access) trackOnce(`chat-pilot:${hero.slug}:${me.id}`, "pilot_started", { screen: "chat" });
    player.stop();
    // С мышью и клавиатурой сразу можно писать; на телефоне фокус открыл бы клавиатуру
    if (window.matchMedia?.("(pointer: fine)").matches) composer.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Вернулись из оплаты: ждём, пока /me увидит подписку (вебхук Stripe приходит не сразу)
  const [confirmCheckout] = useState(route.checkout === "success");
  useEffect(() => {
    if (!confirmCheckout) return;
    navigate(href.chat(), { replace: true });
    let cancelled = false;
    void (async () => {
      toast.show(t("messenger.welcome.pending"), 3_000);
      for (let attempt = 0; attempt < CONFIRM_ATTEMPTS && !cancelled; attempt += 1) {
        const fresh = await reloadMe().catch(() => null);
        if (cancelled) return;
        if (fresh && hasAccess(fresh)) {
          clearPaymentRequired();
          const trialDays = trialDaysFor(hero.pricing, true);
          toast.show(
            fresh.subscription?.status === "trialing"
              ? t("messenger.welcome.trialing", { days: trialDays, hero: hero.name })
              : t("messenger.welcome.active", { hero: hero.name }),
          );
          return;
        }
        await wait(CONFIRM_DELAY_MS);
      }
      if (!cancelled) toast.show(t("player.welcome.late"), 6_000);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmCheckout]);

  // --- прокрутка -------------------------------------------------------------------

  const scrollToBottom = useCallback((smooth: boolean) => {
    const element = scroller.current;
    if (!element) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    element.scrollTo({ top: element.scrollHeight, behavior: smooth && !reduce ? "smooth" : "auto" });
    atBottom.current = true;
    setPinnedToBottom(true);
    setUnread(0);
  }, []);

  const onScroll = () => {
    const element = scroller.current;
    if (!element) return;
    const near = element.scrollHeight - element.scrollTop - element.clientHeight < BOTTOM_SLACK_PX;
    atBottom.current = near;
    setPinnedToBottom(near);
    if (near) setUnread(0);
  };

  // Первый показ — сразу вниз; дальше — вниз, только если человек и так был внизу
  // (или это его собственное сообщение)
  const firstPaint = useRef(true);
  const lastKey = entries.at(-1)?.key ?? "";
  useLayoutEffect(() => {
    if (entries.length === 0) return;
    if (firstPaint.current) {
      firstPaint.current = false;
      scrollToBottom(false);
      return;
    }
    if (forceScroll.current || atBottom.current) {
      forceScroll.current = false;
      scrollToBottom(true);
    }
  }, [lastKey, entries.length, scrollToBottom]);

  // «Печатает» появился или исчез внизу — держим низ
  useLayoutEffect(() => {
    if (atBottom.current) scrollToBottom(false);
  }, [messenger.typing, scrollToBottom]);

  // Картинки догружаются и меняют высоту — если человек внизу, остаёмся внизу
  useEffect(() => {
    const content = log.current;
    if (!content || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (atBottom.current) scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  const keepBottom = useRef(() => {
    if (atBottom.current) scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
  });
  useChatViewport(root, keepBottom.current);

  // --- действия ----------------------------------------------------------------------

  const handlers: BubbleHandlers = useMemo(
    () => ({
      onOpenPhoto: (url, trigger) => setPhoto({ url, trigger }),
      onRetry: (id) => messenger.retry(id),
      onDiscard: (id) => messenger.discard(id),
      onPlay: () => player.stop(),
      onMediaError: messenger.refreshMedia,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messenger.retry, messenger.discard, messenger.refreshMedia],
  );

  const heroFace = { name: hero.name, portraitUrl: hero.theme.portraitUrl };
  const episodeNow = currentEpisode(messenger.items);
  const theo = hero.slug === "theo" || hero.name.toLowerCase() === "theo";
  const status = messenger.typing
    ? typingText(messenger.typing)
    : episodeNow
      ? [t("episode.number", { n: episodeNow }), episodeTitles.get(episodeNow)].filter(Boolean).join(" · ")
      : theo && hero.currentSeason
        ? `${hero.currentSeason.title} · ${hero.tagline}`
        : hero.tagline;

  const notice = messenger.notice ? t("messenger.slowDown") : messenger.reconnecting ? t("messenger.reconnecting") : null;

  let dock: ReactNode;
  if (messenger.fatal === "unauthorized") {
    dock = (
      <div className="chat-signed-out" role="status">
        <p>{t("messenger.signedOut")}</p>
        <button type="button" className="btn btn--primary btn--sm" onClick={() => { setMe(null); navigate(href.signin(href.chat())); }}>
          {t("messenger.signIn")}
        </button>
      </div>
    );
  } else if (messenger.fatal === "not_found") {
    dock = <div className="chat-signed-out" role="status"><p>{t("app.missing.body")}</p></div>;
  } else if (paywall) {
    dock = <PaywallDock hero={hero} me={me} episode={paywall.episode} />;
  } else if (messenger.startError) {
    dock = <div className="chat-signed-out" role="alert"><p>{messenger.startError}</p><button type="button" className="btn btn--primary btn--sm" onClick={messenger.retryStart}>{t("app.retry")}</button></div>;
  } else {
    dock = (
      <Composer
        ref={composer}
        heroName={hero.name}
        heroSlug={hero.slug}
        userId={me.id}
        maxLength={MAX_LENGTH}
        notice={notice}
        onSendText={(text) => {
          forceScroll.current = true;
          messenger.sendText(text);
        }}
        onSendVoice={(recording) => {
          forceScroll.current = true;
          messenger.sendVoice(recording);
        }}
        onNotice={(text) => toast.show(text)}
        onResize={() => keepBottom.current()}
      />
    );
  }

  const empty = messenger.loaded && entries.length === 0;

  return (
    <div className={`chat${theo ? " chat--theo" : ""}`} ref={root}>
      <header className="chat-head">
        <div className="chat-head__inner">
          <Link to={href.hero()} className="chat-head__back" aria-label={t("nav.home", { hero: hero.name })}>
            <Icon name="chevronLeft" size={22} />
          </Link>
          <Link to={href.hero()} className="chat-head__who" aria-label={t("messenger.aside.page", { hero: hero.name })}>
            <Avatar src={hero.theme.portraitUrl} name={hero.name} size={38} />
            <span className="chat-head__text">
              <h1 className="chat-head__name">{hero.name}</h1>
              <span className={`chat-head__status${messenger.typing ? " chat-head__status--typing" : ""}`}>{status}</span>
            </span>
          </Link>
          <Link to={href.account()} className="topbar__me" aria-label={t("nav.account")} title={me.email}>
            <span className="avatar avatar--letter" style={{ width: 34, height: 34, fontSize: 15 }}>
              {initial(me.displayName)}
            </span>
          </Link>
        </div>
      </header>

      <div className="chat__body">
        <ChatAside me={me} subscribed={subscribed} episode={episodeNow} />
        <main className="chat__main" aria-label={t("messenger.log", { hero: hero.name })}>
          <div className="chat__viewport">
            <div className="chat__scroll" ref={scroller} onScroll={onScroll}>
              <ol
                ref={log}
                className="chat__log"
                role="log"
                // Объявляет новое отдельная область ниже: иначе при загрузке история
                // зачитывалась бы целиком
                aria-live="off"
                aria-label={t("messenger.log", { hero: hero.name })}
                aria-busy={!messenger.loaded}
              >
                {!messenger.loaded && (
                  <li className="chat-loading">
                    <Spinner label={t("messenger.loading")} />
                  </li>
                )}
                {empty && !messenger.typing && (
                  <li className="chat-empty">
                    <Avatar src={hero.theme.portraitUrl} name={hero.name} size={72} />
                    <p className="chat-empty__title">{t("messenger.empty.title", { hero: hero.name })}</p>
                    <p className="muted">{t("messenger.empty.body")}</p>
                  </li>
                )}
                {entries.map((entry) => {
                  switch (entry.type) {
                    case "day":
                      return <DayRow key={entry.key} label={entry.label} />;
                    case "episode":
                      return <EpisodeRow key={entry.key} episode={entry.episode} title={entry.title} />;
                    case "notice":
                      return <NoticeRow key={entry.key} tone={entry.tone} text={entry.text} time={entry.time} />;
                    case "paywall":
                      return <PaywallRow key={entry.key} />;
                    case "message":
                      return <MessageRow key={entry.key} entry={entry} hero={heroFace} handlers={handlers} />;
                  }
                })}
                {messenger.typing && <TypingRow kind={messenger.typing} hero={heroFace} />}
              </ol>
            </div>

            {!pinnedToBottom && (
              <button type="button" className={`chat-jump${unread > 0 ? " chat-jump--unread" : ""}`} onClick={() => scrollToBottom(true)}>
                <Icon name="arrowDown" size={18} />
                {unread > 0 ? tn("messenger.newMessages", unread) : <span className="sr-only">{t("messenger.jumpToLatest")}</span>}
              </button>
            )}
          </div>

          <footer className="chat__dock">{dock}</footer>
        </main>
      </div>

      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>

      {photo && (
        <Lightbox
          url={photo.url}
          label={t("messenger.lightbox", { hero: hero.name })}
          returnFocus={photo.trigger}
          onClose={() => setPhoto(null)}
        />
      )}
    </div>
  );
}

/** Широкий экран: слева — кто это, сезон и подписка; на телефоне этого места нет. */
function ChatAside({ me, subscribed, episode }: { me: Me; subscribed: boolean; episode: number | null }) {
  const { hero, episodes } = useHeroData();
  const trialDays = trialDaysFor(hero.pricing, me.trialEligible);
  const season = hero.currentSeason;
  const theo = hero.slug === "theo" || hero.name.toLowerCase() === "theo";
  const storyEpisode = episode ?? 1;
  const storyTitle = episodes.find((item) => item.ordinal === storyEpisode)?.title;
  const storyProgress = season ? Math.min(100, Math.max(8, (storyEpisode / season.episodes) * 100)) : 0;
  return (
    <aside className="chat-aside" aria-label={t("messenger.aside.page", { hero: hero.name })}>
      <div className="chat-aside__visual">
        <Avatar src={hero.theme.portraitUrl} name={hero.name} size={theo ? 156 : 104} className="chat-aside__portrait" />
        {theo && <span className="chat-aside__online" aria-hidden="true" />}
      </div>
      <p className="eyebrow">{theo ? "Interactive text story" : t("messenger.aside.format")}</p>
      <p className="chat-aside__name">{hero.name}</p>
      <p className="chat-aside__tagline">{hero.tagline}</p>
      {season && (
        <p className="chat-aside__season">
          {t("hero.season", { n: season.ordinal })} · {season.title} · {tn("hero.episodesCount", season.episodes)}
        </p>
      )}
      {theo && season && (
        <section className="chat-aside__story" aria-label="Story progress">
          <span>Your story</span>
          <strong>{season.title}</strong>
          <p>Chapter {storyEpisode} of {season.episodes}{storyTitle ? ` · ${storyTitle}` : ""}</p>
          <div className="chat-aside__progress" aria-hidden="true"><i style={{ width: `${storyProgress}%` }} /></div>
        </section>
      )}
      {subscribed ? (
        <p className="status chat-aside__status">
          <Icon name="check" size={14} /> {t("messenger.aside.subscribed")}
        </p>
      ) : (
        <div className="chat-aside__offer">
          {hero.freeEpisodes > 0 && <p className="hint">{tn("messenger.aside.freeNote", hero.freeEpisodes)}</p>}
          <Link to={href.plans()} className="btn btn--outline btn--sm">
            {trialDays > 0 ? t("paywall.cta.trial", { days: trialDays }) : t("paywall.cta.subscribe")}
          </Link>
        </div>
      )}
      <Link to={href.hero()} className="chat-aside__link">
        {theo ? "Back to Theo's page" : t("messenger.aside.page", { hero: hero.name })}
        <Icon name="chevronRight" size={16} />
      </Link>
    </aside>
  );
}
